// Per-disease reference metadata: the gate that decides whether a Mendelian
// mode of inheritance may be asserted at all.
//
// WHY THIS EXISTS
//
// The analyzer previously admitted ANY diagnosis with `displayOnPedigree` to
// Mendelian pattern inference. Nothing consulted what the disease actually is,
// so a multifactorial susceptibility locus and a somatically-acquired neoplasm
// were scored by exactly the same rules as a single-gene disorder — producing
// "Autosomal dominant, Asthma susceptibility to, Strong" and "Autosomal
// dominant, Primary myelofibrosis, Vertical transmission, Strong". A mode of
// inheritance is not a meaningful claim about either.
//
// THE GATE (TGCLOUD-1712, decision DR-1) — layered, in this order:
//
//   1. A clinician's recorded `inheritanceMode` on the diagnosis OVERRIDES in
//      both directions. They know the patient; a reference annotation does not.
//   2. Otherwise, deny on the source's own naming conventions — OMIM marks
//      susceptibility loci "…, susceptibility to" and somatic entries
//      "…, somatic".
//   3. Otherwise, require the reference data to positively settle it: at least
//      one Mendelian mode-of-inheritance annotation, and no unresolved conflict
//      with a non-Mendelian one.
//   4. Anything else asserts NO mode. It is reported as familial aggregation
//      instead, which states the observation without claiming a mechanism.
//
// MEASURED IMPACT, so nobody enables this blind: of the 12,909 OMIM/ORPHA
// diseases in the terminology mirror, 8,384 (64.9%) carry a mode-of-inheritance
// annotation and 4,525 (35.1%) carry none. Diagnoses coded in ICD-10 or a local
// vocabulary have no HPO annotations at all. So a large minority of diseases
// will stop showing an inheritance mode and start showing familial aggregation.
// That is the intended, conservative behaviour — but it is why this ships behind
// `inheritanceCandidateGate`, default off.
//
// This module is PURE. The reference metadata is fetched outside and passed in,
// so the analyzer keeps its no-I/O contract.

export const DISEASE_ASSERTION = Object.freeze({
  // The reference data settles on one or more Mendelian modes.
  mendelian: 'mendelian',
  // The reference data says explicitly non-Mendelian (e.g. HP:0001426).
  nonMendelian: 'non-mendelian',
  // Annotated BOTH ways and unresolved. Real and common: OMIM:600807
  // "Asthma, susceptibility to" carries autosomal dominant AND non-Mendelian,
  // which is the whole of defect 1 in a single row — any code taking the first
  // annotation gets "autosomal dominant" for a susceptibility locus.
  conflicting: 'conflicting',
  // No mode-of-inheritance annotation exists. NOT the same as non-Mendelian:
  // it means the reference is silent, so nothing may be asserted either way.
  unknown: 'unknown'
});

// What the analyzer emits per disease.
export const OUTPUT_KIND = Object.freeze({
  // A mode of inheritance, with its supporting evidence.
  inheritancePattern: 'inheritance_pattern',
  // Relatives share a diagnosis, but no mechanism is claimed. Deliberately a
  // DIFFERENT concept, not a weaker version of the same one.
  familialAggregation: 'familial_aggregation'
});

// Why a mode could not be asserted. Surfaced to the clinician so the absence is
// explained rather than looking like a gap in the software.
export const NO_MODE_REASON = Object.freeze({
  clinicianSporadic: 'clinician_recorded_sporadic',
  susceptibilityLocus: 'susceptibility_locus',
  somaticEntry: 'somatic_entry',
  referenceNonMendelian: 'reference_non_mendelian',
  referenceConflicting: 'reference_conflicting',
  referenceSilent: 'reference_has_no_inheritance_annotation'
});

// Germline vs somatic origin of a recorded variant. A somatic driver is not
// heritable, so it must not be read as molecular evidence of a transmitted
// Mendelian cause — which is exactly what a JAK2 V617F on a myelofibrosis
// patient used to do.
export const VARIANT_ORIGIN = Object.freeze({
  germline: 'germline',
  somatic: 'somatic',
  unknown: 'unknown'
});

// Clinician-recorded inheritance values that positively DENY a Mendelian mode.
// Sourced from the diagnosis form's own option list.
const CLINICIAN_DENIES = new Set(['sporadic', 'multifactorial', 'polygenic']);

// Clinician-recorded values that positively ASSERT one.
const CLINICIAN_ASSERTS = new Set([
  'autosomal dominant',
  'autosomal recessive',
  'x-linked',
  'x-linked dominant',
  'x-linked recessive',
  'y-linked',
  'mitochondrial',
  'chromosomal'
]);

// OMIM/ORPHA naming conventions that mark an entry as not a Mendelian disease.
// Matching on the source's own words is crude but it is the source's own
// statement about the entry, and it catches entries whose annotations are
// missing or contradictory.
const NON_MENDELIAN_NAME = [
  { re: /susceptib/i, reason: NO_MODE_REASON.susceptibilityLocus },
  { re: /,\s*somatic\b/i, reason: NO_MODE_REASON.somaticEntry },
  { re: /\bsomatic\s+mosaic/i, reason: NO_MODE_REASON.somaticEntry }
];

const norm = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/**
 * May a Mendelian mode of inheritance be asserted for this disease?
 *
 * @param {object|null} meta per-disease metadata:
 *   { id, name, assertion, safeToAssertMendelian, clinicianMode }
 * @returns {{ allowed: boolean, reason: string|null, source: string }}
 *   `source` records WHICH layer decided, so the card can explain itself and a
 *   reviewer can tell a clinician's judgement from a reference lookup.
 */
export function canAssertMendelianMode(meta) {
  if (!meta) {
    // No metadata at all — the fetch failed or the disease is unknown to the
    // reference. Fail safe: state the observation, claim no mechanism.
    return {
      allowed: false,
      reason: NO_MODE_REASON.referenceSilent,
      source: 'no_metadata'
    };
  }

  // 1. The clinician's own recorded mode wins, both ways.
  const clinician = norm(meta.clinicianMode);
  if (clinician) {
    if (CLINICIAN_DENIES.has(clinician)) {
      return {
        allowed: false,
        reason: NO_MODE_REASON.clinicianSporadic,
        source: 'clinician'
      };
    }
    if (CLINICIAN_ASSERTS.has(clinician)) {
      return { allowed: true, reason: null, source: 'clinician' };
    }
    // An unrecognised free-text value is not a decision — fall through.
  }

  // 2. The source's own naming.
  const name = typeof meta.name === 'string' ? meta.name : '';
  for (const { re, reason } of NON_MENDELIAN_NAME) {
    if (re.test(name)) {
      return { allowed: false, reason, source: 'disease_name' };
    }
  }

  // 3. The reference annotations must positively settle it.
  if (meta.safeToAssertMendelian === true) {
    return { allowed: true, reason: null, source: 'reference' };
  }

  const reason =
    meta.assertion === DISEASE_ASSERTION.nonMendelian
      ? NO_MODE_REASON.referenceNonMendelian
      : meta.assertion === DISEASE_ASSERTION.conflicting
      ? NO_MODE_REASON.referenceConflicting
      : NO_MODE_REASON.referenceSilent;

  return { allowed: false, reason, source: 'reference' };
}

// ---------------------------------------------------------------------------
// Sex-limited diseases (decision DR-3)
// ---------------------------------------------------------------------------
//
// A disease that can only affect one sex makes "no male-to-male transmission"
// and "only males affected" vacuous: the disconfirming observation was never
// possible, so its absence is not evidence. Prostate cancer with two affected
// males became "X-linked recessive, Strong" purely because no affected female
// could exist.
//
// The structural observability check in the detectors is the general fix. This
// list closes the residual case it cannot reach — an affected uncle and nephew
// whose fathers ARE recorded and unaffected, where male-to-male transmission
// was structurally possible but is biologically irrelevant.
//
// Seeded per DR-3. This SHOULD become clinician-maintainable configuration;
// until then, adding to it is a code change and deliberately visible in review.
const SEX_LIMITED_KEYWORDS = [
  { re: /\bprostat/i, sex: 'M' },
  { re: /\btesticular\b|\btestis\b/i, sex: 'M' },
  { re: /\bovarian\b|\bovary\b/i, sex: 'F' },
  { re: /\buterine\b|\buterus\b|\bendometrial\b/i, sex: 'F' },
  { re: /\bcervical cancer\b|\bcervix\b/i, sex: 'F' },
  { re: /\bvulval?\b|\bvaginal\b/i, sex: 'F' }
];

/**
 * The sex a disease is limited to, or null when it is not sex-limited.
 * @returns {'M'|'F'|null}
 */
export function sexLimitedTo(meta) {
  const name = meta && typeof meta.name === 'string' ? meta.name : '';
  if (!name) return null;
  for (const { re, sex } of SEX_LIMITED_KEYWORDS) {
    if (re.test(name)) return sex;
  }
  return null;
}

/** Convenience predicate. */
export const isSexLimited = (meta) => sexLimitedTo(meta) !== null;

/**
 * Normalise one entry from the terminology service's inheritance lookup into
 * the metadata shape above, merging in the clinician's recorded value.
 *
 * Kept here so the fetch layer stays dumb and the contract lives in one place.
 */
export function toDiseaseMeta(referenceEntry, { name, clinicianMode } = {}) {
  return {
    id: referenceEntry?.id || null,
    name: name || referenceEntry?.name || '',
    assertion: referenceEntry?.assertion || DISEASE_ASSERTION.unknown,
    safeToAssertMendelian: referenceEntry?.safeToAssertMendelian === true,
    modes: Array.isArray(referenceEntry?.modes) ? referenceEntry.modes : [],
    // Uppercase HGNC symbols associated with this disease. The molecular
    // upgrade rule reads this to check that a pathogenic variant is in a gene
    // actually associated with THIS disease — without it, a BRCA1 carrier who
    // also has asthma upgraded asthma to autosomal dominant.
    //
    // ALWAYS AN ARRAY. Omitting it here left every meta with `undefined`,
    // which the rule reads as "no link established" — so the upgrade never
    // fired for any disease and the fix was silently inert.
    associatedGenes: Array.isArray(referenceEntry?.associatedGenes)
      ? referenceEntry.associatedGenes
      : [],
    clinicianMode: clinicianMode || null
  };
}
